import React, { useState, useEffect } from 'react';
import { Search, Edit2, Save, X, ChevronUp, ChevronDown, Plus } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { Customer } from '../types';
import { formatBytes, getProgressBarColor } from '../utils/formatters';
import { TEXT } from '../constants/localization';
import { API_ENDPOINTS } from '../config/api';
import { useAuthWithRole } from '../hooks/useAuthWithRole';

const CustomerTable: React.FC = () => {
  const { userRole, isAdmin } = useAuthWithRole();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<Customer>>({});
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<keyof Customer>('station_code');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    station_code: '',
    partition_server_num: 1,
    current_server_num: 1,
    total: 1073741824, // 1GB default
  });

  useEffect(() => {
    fetchCustomers();
    const interval = setInterval(fetchCustomers, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const filtered = customers.filter(customer =>
      customer.station_code.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomers(filtered);
  }, [customers, searchTerm]);

  const fetchCustomers = async () => {
    try {
      const response = await apiClient.get<Customer[]>(API_ENDPOINTS.CUSTOMERS);
      setCustomers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setLoading(false);
    }
  };

  const handleSort = (field: keyof Customer) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }

    const sorted = [...filteredCustomers].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredCustomers(sorted);
  };

  const handleEdit = (customer: Customer) => {
    setEditingId(customer.station_code);
    setEditData({
      current_server_num: customer.current_server_num,
      total: customer.total,
      used: customer.used,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (station_code: string) => {
    try {
      await apiClient.put(API_ENDPOINTS.CUSTOMER(station_code), editData);
      await fetchCustomers();
      setEditingId(null);
      setEditData({});
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Failed to update customer data');
    }
  };

  const handleAddCustomer = async () => {
    try {
      const customerData = {
        ...newCustomerData,
        used: 0,
        available: newCustomerData.total,
        usage_percent: 0,
        partition_server_num: [newCustomerData.partition_server_num]
      };

      await apiClient.post(API_ENDPOINTS.CREATE_CUSTOMER, customerData);
      await fetchCustomers();
      setShowAddModal(false);
      setNewCustomerData({
        station_code: '',
        partition_server_num: 1,
        current_server_num: 1,
        total: 1073741824,
      });
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Failed to add customer');
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewCustomerData({
      station_code: '',
      partition_server_num: 1,
      current_server_num: 1,
      total: 1073741824,
    });
  };

  const SortIcon = ({ field }: { field: keyof Customer }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <ChevronUp className="inline h-4 w-4" /> :
      <ChevronDown className="inline h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{TEXT.CUSTOMER_STORAGE_MANAGEMENT}</h2>
        <div className="flex items-center gap-4">
          {isAdmin() && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              {TEXT.ADD_NEW_CUSTOMER}
            </button>
          )}
          <div className="relative">
            <input
            type="text"
            placeholder={TEXT.SEARCH_CUSTOMERS}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th
                onClick={() => handleSort('station_code')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
              >
{TEXT.CUSTOMER_ID} <SortIcon field="station_code" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {TEXT.STORAGE_SERVERS}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {TEXT.CURRENT_SERVER}
              </th>
              <th
                onClick={() => handleSort('total')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
              >
{TEXT.TOTAL_SPACE_TABLE} <SortIcon field="total" />
              </th>
              <th
                onClick={() => handleSort('used')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
              >
{TEXT.USED_SPACE} <SortIcon field="used" />
              </th>
              <th
                onClick={() => handleSort('available')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
              >
{TEXT.AVAILABLE_SPACE} <SortIcon field="available" />
              </th>
              <th
                onClick={() => handleSort('usage_percent')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
              >
{TEXT.USAGE} <SortIcon field="usage_percent" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {TEXT.ACTIONS}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredCustomers.map((customer) => (
              <tr key={customer.station_code} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  {customer.station_code}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {Array.isArray(customer.partition_server_num)
                    ? customer.partition_server_num.join(', ')
                    : customer.partition_server_num}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {editingId === customer.station_code ? (
                    <input
                      type="number"
                      value={editData.current_server_num ?? ''}
                      onChange={(e) => setEditData({ ...editData, current_server_num: parseInt(e.target.value) })}
                      className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  ) : (
                    customer.current_server_num
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {editingId === customer.station_code ? (
                    <input
                      type="number"
                      value={editData.total ?? ''}
                      onChange={(e) => setEditData({ ...editData, total: parseInt(e.target.value) })}
                      className="w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  ) : (
                    formatBytes(customer.total)
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {editingId === customer.station_code ? (
                    <input
                      type="number"
                      value={editData.used ?? ''}
                      onChange={(e) => setEditData({ ...editData, used: parseInt(e.target.value) })}
                      className="w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  ) : (
                    formatBytes(customer.used)
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatBytes(customer.available || 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-full max-w-xs">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor(customer.usage_percent || 0)}`}
                            style={{ width: `${customer.usage_percent || 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12 text-right">
                          {customer.usage_percent || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {isAdmin() ? (
                    editingId === customer.station_code ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSave(customer.station_code)}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                        >
                          <Save className="h-5 w-5" />
                        </button>
                        <button
                          onClick={handleCancel}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(customer)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                    )
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 新增客戶模態對話框 */}
      {showAddModal && isAdmin() && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{TEXT.ADD_NEW_CUSTOMER}</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT.CUSTOMER_ID}
                </label>
                <input
                  type="text"
                  value={newCustomerData.station_code}
                  onChange={(e) => setNewCustomerData({...newCustomerData, station_code: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="請輸入客戶 ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT.STORAGE_SERVERS}
                </label>
                <input
                  type="number"
                  value={newCustomerData.partition_server_num}
                  onChange={(e) => setNewCustomerData({...newCustomerData, partition_server_num: parseInt(e.target.value) || 1})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT.CURRENT_SERVER}
                </label>
                <input
                  type="number"
                  value={newCustomerData.current_server_num}
                  onChange={(e) => setNewCustomerData({...newCustomerData, current_server_num: parseInt(e.target.value) || 1})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT.TOTAL_SPACE_TABLE}
                </label>
                <select
                  value={newCustomerData.total}
                  onChange={(e) => setNewCustomerData({...newCustomerData, total: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {[...Array(10)].map((_, i) => {
                    const gb = i + 1;
                    const bytes = gb * 1073741824;
                    return (
                      <option key={gb} value={bytes}>
                        {gb} GB ({bytes.toLocaleString()} bytes)
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCloseAddModal}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium rounded-md transition-colors"
              >
                {TEXT.CANCEL}
              </button>
              <button
                onClick={handleAddCustomer}
                disabled={!newCustomerData.station_code.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors"
              >
                {TEXT.ADD_CUSTOMER}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTable;